import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, Settings, Loader2, CheckCircle, AlertTriangle,
  Info, Send, Calendar, HelpCircle, ChevronDown, ChevronUp, Clock, FileText,
  Key, ShieldCheck, Upload, Play, Square, Download, Eye,
  FileCheck, RefreshCw, Plus, X, Trash2, Activity, TrendingUp,
  Timer, Hash, DollarSign, FileOutput, Shield, Filter, ArrowUpDown,
  ExternalLink, ChevronsUpDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config';

const KSEF_FAQ = [
  { q: "Czym jest KSeF?", a: "Krajowy System e-Faktur (KSeF) to platforma MF do wystawiania i przechowywania faktur ustrukturyzowanych w formacie XML. Podstawa prawna: art. 106na-106nc ustawy o VAT." },
  { q: "Od kiedy KSeF jest obowiazkowy?", a: "Od 1 lutego 2026 r. dla duzych podatnikow (przychody > 200 mln zl), od 1 kwietnia 2026 r. dla pozostalych podatnikow VAT." },
  { q: "Jak uzyskac token KSeF?", a: "1. Zaloguj sie na ksef.mf.gov.pl przez Profil Zaufany lub podpis kwalifikowany. 2. Przejdz do sekcji 'Tokeny'. 3. Wygeneruj nowy token z uprawnieniami do wystawiania i pobierania faktur. 4. Skopiuj token (wyswietla sie tylko raz!)." },
  { q: "Jakie kary za niekorzystanie z KSeF?", a: "Kara pieniezna do 100% kwoty VAT wykazanej na fakturze wystawionej poza KSeF (min. 1000 zl). W okresie przejsciowym kary nie beda naliczane." },
  { q: "Czy KSeF dziala w trybie offline?", a: "Tak, w przypadku awarii mozna wystawic fakture offline. Nalezy ja przeslac do KSeF w ciagu 7 dni od ustania przyczyny." },
  { q: "Czym jest UPO?", a: "Urzedowe Poswiadczenie Odbioru - potwierdzenie przyjecia faktury przez KSeF. Stanowi dowod wystawienia faktury w systemie." },
];

const KSEF_DEADLINES = [
  { date: "01.02.2026", title: "KSeF obowiazkowy - duzi podatnicy", desc: "Przychody > 200 mln zl", priority: "high" },
  { date: "01.04.2026", title: "KSeF obowiazkowy - pozostali", desc: "Wszyscy podatnicy VAT", priority: "high" },
  { date: "01.07.2026", title: "Kary za brak KSeF", desc: "Poczatek naliczania kar", priority: "medium" },
];

export default function KsefPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [openFaq, setOpenFaq] = useState(null);

  // Config state
  const [nip, setNip] = useState('');
  const [authType, setAuthType] = useState('token');
  const [ksefToken, setKsefToken] = useState('');
  const [saving, setSaving] = useState(false);

  // Session state
  const [sessionLoading, setSessionLoading] = useState(false);

  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '', seller_name: '', seller_nip: '', seller_address: '',
    buyer_name: '', buyer_nip: '', buyer_address: '', issue_date: new Date().toISOString().split('T')[0],
    payment_date: '', bank_account: '', currency: 'PLN',
    items: [{ name: '', quantity: 1, unit: 'szt.', unit_price: 0, net_amount: 0, vat_rate: 23 }]
  });
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [generatedXml, setGeneratedXml] = useState('');

  // Classify state
  const [classifyText, setClassifyText] = useState('');
  const [classifyResult, setClassifyResult] = useState(null);
  const [classifyLoading, setClassifyLoading] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadDateFrom, setDownloadDateFrom] = useState('');
  const [downloadDateTo, setDownloadDateTo] = useState('');
  const [downloadSubjectType, setDownloadSubjectType] = useState('');
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [sortBy, setSortBy] = useState('issue_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterDirection, setFilterDirection] = useState('');
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadStatus(); loadInvoices(); }, []);
  useEffect(() => { loadInvoices(); }, [sortBy, sortOrder, filterDirection]);

  // Session countdown timer
  useEffect(() => {
    if (!status?.session_info?.valid_until) { setSessionTimeLeft(null); return; }
    const updateTimer = () => {
      const validUntil = new Date(status.session_info.valid_until);
      const now = new Date();
      const diff = validUntil - now;
      if (diff <= 0) { setSessionTimeLeft({ expired: true, text: 'Wygasla' }); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setSessionTimeLeft({
        expired: false,
        hours: h, minutes: m, seconds: s,
        text: `${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`,
        percentage: Math.max(0, Math.min(100, (diff / (24 * 3600000)) * 100)),
        urgent: diff < 1800000 // less than 30 min
      });
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [status?.session_info?.valid_until]);

  const loadStatus = async () => {
    try {
      const resp = await axios.get(`${API_URL}/ksef/status`, { headers });
      setStatus(resp.data);
      setNip(resp.data.nip || '');
      setAuthType(resp.data.auth_type || 'token');
    } catch { toast.error('Blad ladowania statusu KSeF'); }
    finally { setLoading(false); }
  };

  const loadInvoices = async () => {
    try {
      const params = new URLSearchParams({ sort_by: sortBy, sort_order: sortOrder });
      if (filterDirection) params.append('direction', filterDirection);
      const resp = await axios.get(`${API_URL}/ksef/invoices?${params}`, { headers });
      setInvoices(resp.data || []);
    } catch {}
  };

  const handlePreviewInvoice = async (invoiceId) => {
    setPreviewLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/ksef/invoices/${invoiceId}/preview`, { headers });
      setPreviewInvoice(resp.data);
    } catch { toast.error('Blad podgladu faktury'); }
    finally { setPreviewLoading(false); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); }
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const handleSaveConfig = async () => {
    if (!nip || nip.length !== 10) { toast.error('NIP musi miec 10 cyfr'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/ksef/configure`, {
        nip, enabled: true, auth_type: authType, token: ksefToken
      }, { headers });
      toast.success('Konfiguracja KSeF zapisana');
      setKsefToken('');
      loadStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad konfiguracji'); }
    finally { setSaving(false); }
  };

  const handleUploadCert = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('password', '');
    try {
      await axios.post(`${API_URL}/ksef/upload-certificate`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Certyfikat zapisany');
      loadStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad uploadu certyfikatu'); }
  };

  const handleOpenSession = async () => {
    setSessionLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/ksef/session/open`, {}, { headers, timeout: 60000 });
      if (resp.data.success) {
        toast.success('Sesja KSeF otwarta');
        loadStatus();
      } else {
        toast.error(resp.data.error || 'Blad otwierania sesji');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad polaczenia z KSeF'); }
    finally { setSessionLoading(false); }
  };

  const handleCloseSession = async () => {
    setSessionLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/ksef/session/close`, {}, { headers });
      if (resp.data.success) toast.success('Sesja zamknieta');
      else toast.error(resp.data.error || 'Blad zamykania sesji');
      loadStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSessionLoading(false); }
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      newItems[idx].net_amount = (parseFloat(newItems[idx].quantity) || 0) * (parseFloat(newItems[idx].unit_price) || 0);
    }
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const addItem = () => {
    setInvoiceForm({ ...invoiceForm, items: [...invoiceForm.items, { name: '', quantity: 1, unit: 'szt.', unit_price: 0, net_amount: 0, vat_rate: 23 }] });
  };

  const removeItem = (idx) => {
    if (invoiceForm.items.length <= 1) return;
    setInvoiceForm({ ...invoiceForm, items: invoiceForm.items.filter((_, i) => i !== idx) });
  };

  const handleGenerateXml = async () => {
    try {
      const resp = await axios.post(`${API_URL}/ksef/invoices/generate-xml`, { invoice_data: invoiceForm }, { headers });
      setGeneratedXml(resp.data.xml);
      if (resp.data.validation?.valid) toast.success('XML wygenerowany i poprawny');
      else toast.warning('XML wygenerowany z ostrzezeniami');
    } catch (err) { toast.error('Blad generowania XML'); }
  };

  const handleSendInvoice = async () => {
    setSendingInvoice(true);
    try {
      const resp = await axios.post(`${API_URL}/ksef/invoices/send`, {
        invoice_data: invoiceForm, invoice_xml: generatedXml || undefined
      }, { headers, timeout: 60000 });
      if (resp.data.success) {
        toast.success(`Faktura wyslana do KSeF! Nr: ${resp.data.ksef_number || 'przetwarzanie...'}`);
        loadInvoices();
        setGeneratedXml('');
      } else {
        toast.error(resp.data.error || 'Blad wysylki');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad wysylki faktury'); }
    finally { setSendingInvoice(false); }
  };

  const handleClassify = async () => {
    if (!classifyText.trim()) return;
    setClassifyLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/ksef/classify-invoice`, { invoice_data: classifyText }, { headers, timeout: 60000 });
      setClassifyResult(resp.data.classification);
    } catch { toast.error('Blad klasyfikacji'); }
    finally { setClassifyLoading(false); }
  };

  const handleDownloadInvoices = async () => {
    if (!status?.session_active) {
      toast.error('Otworz sesje KSeF aby pobierac faktury');
      return;
    }
    setDownloadLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/ksef/invoices/download`, {
        date_from: downloadDateFrom || undefined,
        date_to: downloadDateTo || undefined,
        subject_type: downloadSubjectType || undefined
      }, { headers, timeout: 60000 });
      if (resp.data.success) {
        const count = resp.data.data?.invoices?.length || resp.data.data?.items?.length || 0;
        toast.success(`Pobrano ${count} faktur z KSeF`);
        loadInvoices();
        setShowDownloadPanel(false);
      } else {
        toast.error(resp.data.error || 'Blad pobierania faktur z KSeF');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad polaczenia z KSeF');
    }
    finally { setDownloadLoading(false); }
  };

  const handleCheckInvoiceStatus = async (invoiceRef) => {
    if (!status?.session_active || !invoiceRef) return;
    try {
      const resp = await axios.get(`${API_URL}/ksef/invoices/${invoiceRef}/status`, { headers });
      if (resp.data.success) {
        toast.success('Status faktury zaktualizowany');
        loadInvoices();
      } else {
        toast.error(resp.data.error || 'Blad sprawdzania statusu');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad');
    }
  };

  const netTotal = invoiceForm.items.reduce((s, i) => s + (parseFloat(i.net_amount) || 0), 0);
  const vatTotal = invoiceForm.items.reduce((s, i) => s + ((parseFloat(i.net_amount) || 0) * (parseFloat(i.vat_rate) || 0) / 100), 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const tabs = [
    { id: 'overview', label: 'Przeglad', icon: Zap },
    { id: 'config', label: 'Konfiguracja', icon: Settings },
    { id: 'invoices', label: 'Faktury', icon: FileText },
    { id: 'send', label: 'Wystaw fakture', icon: Send },
    { id: 'classify', label: 'Klasyfikacja AI', icon: FileCheck },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      {/* Header */}
      <div className="border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="gap-2 text-slate-500">
              <ArrowLeft className="w-4 h-4" /> Powrot
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 dark:text-white">KSeF 2.0</h1>
                <p className="text-xs text-slate-400">Krajowy System e-Faktur</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status?.session_active ? (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid="ksef-session-badge">Sesja aktywna</span>
            ) : status?.has_credentials ? (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Skonfigurowany</span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Nieskonfigurowany</span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-8">
        {/* Tabs */}
        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <Button key={tab.id} variant={activeTab === tab.id ? 'default' : 'ghost'} size="sm"
              onClick={() => setActiveTab(tab.id)} data-testid={`ksef-tab-${tab.id}`}
              className={`gap-1.5 whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-slate-600'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </Button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">

            {/* Session Status Panel */}
            <div className={`rounded-2xl border-2 p-6 shadow-sm transition-all ${
              status?.session_active
                ? sessionTimeLeft?.urgent
                  ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 dark:from-amber-900/20 dark:to-orange-900/20'
                  : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300 dark:from-emerald-900/20 dark:to-teal-900/20'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
            }`} data-testid="ksef-session-status-panel">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                    status?.session_active
                      ? sessionTimeLeft?.urgent ? 'bg-amber-500' : 'bg-emerald-500'
                      : 'bg-slate-400'
                  }`}>
                    {status?.session_active ? <Activity className="w-6 h-6 text-white" /> : <Shield className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <h2 className="font-bold text-lg text-slate-800 dark:text-white">Status sesji KSeF</h2>
                    <p className="text-sm text-slate-500">
                      {status?.session_active ? 'Sesja interaktywna aktywna' : 'Brak aktywnej sesji'}
                    </p>
                  </div>
                </div>
                {status?.session_active && (
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                    sessionTimeLeft?.urgent ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'
                  }`} data-testid="ksef-session-live-badge">
                    <span className={`w-2 h-2 rounded-full animate-pulse ${sessionTimeLeft?.urgent ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                    LIVE
                  </div>
                )}
              </div>

              {status?.session_active && status?.session_info ? (
                <div className="space-y-4">
                  {/* Countdown timer */}
                  <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-xl p-4 border border-white/50" data-testid="ksef-session-countdown">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                        <Timer className="w-3.5 h-3.5" /> Czas do wygasniecia sesji
                      </span>
                      {sessionTimeLeft && !sessionTimeLeft.expired && (
                        <span className={`text-xs font-medium ${sessionTimeLeft.urgent ? 'text-amber-600' : 'text-slate-400'}`}>
                          {sessionTimeLeft.urgent ? 'Sesja zaraz wygasnie!' : ''}
                        </span>
                      )}
                    </div>
                    {sessionTimeLeft && !sessionTimeLeft.expired ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-3">
                          <div className="flex gap-2">
                            <div className="text-center">
                              <span className={`text-3xl font-bold tabular-nums ${sessionTimeLeft.urgent ? 'text-amber-600' : 'text-emerald-700'}`}>
                                {String(sessionTimeLeft.hours).padStart(2, '0')}
                              </span>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wider">godz</p>
                            </div>
                            <span className="text-3xl font-bold text-slate-300 self-start">:</span>
                            <div className="text-center">
                              <span className={`text-3xl font-bold tabular-nums ${sessionTimeLeft.urgent ? 'text-amber-600' : 'text-emerald-700'}`}>
                                {String(sessionTimeLeft.minutes).padStart(2, '0')}
                              </span>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wider">min</p>
                            </div>
                            <span className="text-3xl font-bold text-slate-300 self-start">:</span>
                            <div className="text-center">
                              <span className={`text-3xl font-bold tabular-nums ${sessionTimeLeft.urgent ? 'text-amber-600' : 'text-emerald-700'}`}>
                                {String(sessionTimeLeft.seconds).padStart(2, '0')}
                              </span>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wider">sek</p>
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-slate-200/80 rounded-full h-1.5 overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-1000 ${
                            sessionTimeLeft.urgent ? 'bg-amber-500' : 'bg-emerald-500'
                          }`} style={{ width: `${sessionTimeLeft.percentage}%` }} />
                        </div>
                      </>
                    ) : (
                      <p className="text-lg font-bold text-red-600">Sesja wygasla</p>
                    )}
                  </div>

                  {/* Session details grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-xl p-3 border border-white/50">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1"><Hash className="w-3 h-3" />Nr referencyjny</p>
                      <p className="text-sm font-mono font-medium text-slate-700 dark:text-slate-200 truncate" data-testid="ksef-session-ref">
                        {status.session_info.reference_number || '-'}
                      </p>
                    </div>
                    <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-xl p-3 border border-white/50">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1"><Clock className="w-3 h-3" />Otwarta o</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200" data-testid="ksef-session-opened">
                        {status.session_info.opened_at ? new Date(status.session_info.opened_at).toLocaleString('pl-PL', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-300">
                  <Shield className="w-8 h-8 text-slate-300" />
                  <div>
                    <p className="text-sm font-medium text-slate-600">Sesja nieaktywna</p>
                    <p className="text-xs text-slate-400">Otworz sesje w zakladce Konfiguracja, aby wysylac faktury.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ksef-invoice-stats">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Send className="w-4.5 h-4.5 text-blue-600" />
                  </div>
                  <p className="text-xs text-slate-500 font-medium">Wyslane</p>
                </div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="ksef-stat-sent">{status?.invoice_stats?.sent || 0}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">faktur sprzedazy</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <Download className="w-4.5 h-4.5 text-green-600" />
                  </div>
                  <p className="text-xs text-slate-500 font-medium">Pobrane</p>
                </div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="ksef-stat-received">{status?.invoice_stats?.received || 0}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">faktur zakupu</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                    <FileText className="w-4.5 h-4.5 text-indigo-600" />
                  </div>
                  <p className="text-xs text-slate-500 font-medium">Lacznie</p>
                </div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="ksef-stat-total">{status?.invoice_count || 0}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">wszystkich faktur</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <TrendingUp className="w-4.5 h-4.5 text-amber-600" />
                  </div>
                  <p className="text-xs text-slate-500 font-medium">Wartosc netto</p>
                </div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="ksef-stat-value">
                  {(status?.invoice_stats?.total_net_value || 0).toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">PLN laczna wartosc</p>
              </div>
            </div>

            {/* Last invoice / UPO */}
            {status?.last_invoice && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm" data-testid="ksef-last-invoice-panel">
                <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200 flex items-center gap-2 mb-4">
                  <FileOutput className="w-4 h-4 text-blue-600" /> Ostatnia faktura / UPO
                </h3>
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/30 rounded-xl">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-800 dark:text-white truncate">
                      {status.last_invoice.invoice_number || 'Brak numeru'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{status.last_invoice.buyer_name || '-'}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {status.last_invoice.sent_at ? new Date(status.last_invoice.sent_at).toLocaleString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-lg text-slate-800 dark:text-white">
                      {status.last_invoice.net_amount ? `${parseFloat(status.last_invoice.net_amount).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zl` : '-'}
                    </p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      status.last_invoice.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      status.last_invoice.status === 'sent' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`} data-testid="ksef-last-invoice-status">
                      {status.last_invoice.status === 'accepted' ? <><CheckCircle className="w-3 h-3" /> UPO</>
                       : status.last_invoice.status === 'sent' ? <><Clock className="w-3 h-3" /> Wyslana</>
                       : status.last_invoice.status || 'N/A'}
                    </span>
                  </div>
                </div>
                {status.last_invoice.invoice_reference && (
                  <p className="text-xs text-slate-400 mt-2 font-mono">
                    Ref: {status.last_invoice.invoice_reference}
                  </p>
                )}
              </div>
            )}

            {/* Deadlines */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
              <h2 className="font-bold text-lg text-slate-800 dark:text-white flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-blue-600" /> Kluczowe terminy
              </h2>
              <div className="space-y-3">
                {KSEF_DEADLINES.map((d, i) => {
                  const deadline = new Date(d.date.split('.').reverse().join('-'));
                  const daysLeft = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={i} className={`flex items-center gap-4 p-4 rounded-xl border ${d.priority === 'high' ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid={`ksef-deadline-${i}`}>
                      <Clock className={`w-5 h-5 ${d.priority === 'high' ? 'text-red-600' : 'text-amber-600'}`} />
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-slate-800">{d.title}</p>
                        <p className="text-xs text-slate-500">{d.desc}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm">{d.date}</p>
                        <p className={`text-xs font-medium ${daysLeft <= 0 ? 'text-red-600' : daysLeft <= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                          {daysLeft > 0 ? `za ${daysLeft} dni` : 'Termin minal'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Alerts */}
            {status?.alerts?.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border p-6 shadow-sm">
                <h3 className="font-bold text-sm mb-3">Ostatnie powiadomienia</h3>
                <div className="space-y-2">
                  {status.alerts.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-slate-50">
                      {a.type === 'success' ? <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" /> : <Info className="w-4 h-4 text-blue-500 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{a.title}</p>
                        <p className="text-xs text-slate-500">{a.description}</p>
                      </div>
                      <span className="text-xs text-slate-400">{a.created_at ? new Date(a.created_at).toLocaleDateString('pl-PL') : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONFIG TAB */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border p-6 shadow-sm">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Key className="w-5 h-5 text-blue-600" /> Dane logowania KSeF</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">NIP firmy</label>
                  <Input value={nip} onChange={e => setNip(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="0000000000" data-testid="ksef-nip-input" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Metoda uwierzytelniania</label>
                  <div className="flex gap-3">
                    <Button variant={authType === 'token' ? 'default' : 'outline'} size="sm" onClick={() => setAuthType('token')}
                      className={`gap-2 ${authType === 'token' ? 'bg-blue-600 text-white' : ''}`} data-testid="auth-type-token">
                      <Key className="w-4 h-4" /> Token
                    </Button>
                    <Button variant={authType === 'certificate' ? 'default' : 'outline'} size="sm" onClick={() => setAuthType('certificate')}
                      className={`gap-2 ${authType === 'certificate' ? 'bg-blue-600 text-white' : ''}`} data-testid="auth-type-cert">
                      <ShieldCheck className="w-4 h-4" /> Certyfikat (.p12)
                    </Button>
                  </div>
                </div>

                {authType === 'token' ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">Token KSeF</label>
                    <Input type="password" value={ksefToken} onChange={e => setKsefToken(e.target.value)}
                      placeholder="Wklej token wygenerowany na ksef.mf.gov.pl" data-testid="ksef-token-input" />
                    <p className="text-xs text-slate-400 mt-1">Token mozesz wygenerowac na ksef.mf.gov.pl &rarr; Tokeny &rarr; Generuj nowy token</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1">Certyfikat kwalifikowany (.p12 / .pfx)</label>
                    <input ref={fileInputRef} type="file" accept=".p12,.pfx" onChange={handleUploadCert} className="hidden" />
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2" data-testid="ksef-cert-upload-btn">
                      <Upload className="w-4 h-4" /> Wybierz certyfikat
                    </Button>
                    {status?.has_credentials && status?.auth_type === 'certificate' && (
                      <p className="text-xs text-green-600 mt-1">Certyfikat zaladowany</p>
                    )}
                  </div>
                )}

                <Button onClick={handleSaveConfig} disabled={saving || !nip} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="ksef-save-config-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Zapisz konfiguracje
                </Button>
              </div>
            </div>

            {/* Session management */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border p-6 shadow-sm">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-green-600" /> Sesja KSeF</h2>
              <p className="text-sm text-slate-500 mb-4">
                Aby wysylac i pobierac faktury, musisz otworzyc sesje interaktywna z KSeF.
                Sesja wymaga skonfigurowanego tokena lub certyfikatu.
              </p>
              <div className="flex gap-3">
                {!status?.session_active ? (
                  <Button onClick={handleOpenSession} disabled={sessionLoading || !status?.has_credentials}
                    className="gap-2 bg-green-600 hover:bg-green-700 text-white" data-testid="ksef-open-session-btn">
                    {sessionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    Otworz sesje
                  </Button>
                ) : (
                  <Button onClick={handleCloseSession} disabled={sessionLoading}
                    className="gap-2 bg-red-500 hover:bg-red-600 text-white" data-testid="ksef-close-session-btn">
                    {sessionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                    Zamknij sesje (pobierz UPO)
                  </Button>
                )}
              </div>
              {!status?.has_credentials && (
                <p className="text-xs text-amber-600 mt-2">Najpierw skonfiguruj dane logowania powyzej.</p>
              )}
            </div>
          </div>
        )}

        {/* INVOICES TAB */}
        {activeTab === 'invoices' && (
          <div className="space-y-4">
            {/* Action bar */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                <h2 className="font-bold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Faktury KSeF</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Direction filter */}
                  <div className="flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                    <select
                      value={filterDirection}
                      onChange={e => setFilterDirection(e.target.value)}
                      className="text-xs border rounded-lg px-2 py-1.5 bg-white dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-blue-500"
                      data-testid="ksef-filter-direction"
                    >
                      <option value="">Wszystkie</option>
                      <option value="sales">Sprzedaz</option>
                      <option value="purchase">Zakup</option>
                    </select>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadInvoices} className="gap-1.5" data-testid="ksef-refresh-invoices-btn">
                    <RefreshCw className="w-3.5 h-3.5" /> Odswiez
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowDownloadPanel(!showDownloadPanel)}
                    disabled={!status?.session_active}
                    className="gap-1.5 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                    data-testid="ksef-download-invoices-btn"
                    title={!status?.session_active ? 'Otworz sesje KSeF aby pobrac faktury' : ''}
                  >
                    <Download className="w-3.5 h-3.5" /> Pobierz z KSeF
                  </Button>
                </div>
              </div>
              {!status?.session_active && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Otworz sesje KSeF w zakladce Konfiguracja, aby pobierac faktury.
                </p>
              )}
            </div>

            {/* Download panel */}
            {showDownloadPanel && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl border-2 border-green-200 dark:border-green-800 p-5 shadow-sm" data-testid="ksef-download-panel">
                <h3 className="font-bold text-sm text-green-800 dark:text-green-200 flex items-center gap-2 mb-4">
                  <Download className="w-4 h-4" /> Pobierz faktury z Krajowego Systemu e-Faktur
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Typ faktur</label>
                    <select
                      value={downloadSubjectType}
                      onChange={(e) => setDownloadSubjectType(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-green-500"
                      data-testid="ksef-download-subject-type"
                    >
                      <option value="">Wszystkie (sprzedaz + zakup)</option>
                      <option value="Subject1">Sprzedaz (wystawione)</option>
                      <option value="Subject2">Zakup (otrzymane)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Data od</label>
                    <input
                      type="date"
                      value={downloadDateFrom}
                      onChange={(e) => setDownloadDateFrom(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="ksef-download-date-from"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Data do</label>
                    <input
                      type="date"
                      value={downloadDateTo}
                      onChange={(e) => setDownloadDateTo(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-800 dark:border-slate-600 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="ksef-download-date-to"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleDownloadInvoices}
                      disabled={downloadLoading}
                      className="gap-1.5 bg-green-600 hover:bg-green-700 text-white flex-1"
                      data-testid="ksef-download-submit-btn"
                    >
                      {downloadLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {downloadLoading ? 'Pobieranie...' : 'Pobierz'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowDownloadPanel(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-green-700 dark:text-green-400 mt-3">
                  Pozostaw daty puste aby pobrac wszystkie dostepne faktury. Pobrane faktury zostana zapisane w systemie.
                </p>
              </div>
            )}

            {/* Invoice list */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border shadow-sm overflow-hidden">
              {invoices.length === 0 ? (
                <div className="text-center py-12 p-5">
                  <FileText className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 mb-2">Brak faktur w systemie.</p>
                  <p className="text-xs text-slate-400">Wyslij fakture w zakladce "Wystaw fakture" lub pobierz z KSeF przyciskiem powyzej.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ksef-invoices-table">
                    <thead>
                      <tr className="border-b bg-slate-50 dark:bg-slate-700/30">
                        {/* Sortable: invoice_number */}
                        <th
                          className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                          onClick={() => toggleSort('invoice_number')}
                          data-testid="ksef-sort-invoice-number"
                        >
                          <div className="flex items-center gap-1.5">
                            Nr faktury
                            <span className="opacity-50 group-hover:opacity-100">
                              {sortBy === 'invoice_number'
                                ? (sortOrder === 'desc' ? <ChevronDown className="w-3.5 h-3.5 text-blue-600 opacity-100" /> : <ChevronUp className="w-3.5 h-3.5 text-blue-600 opacity-100" />)
                                : <ChevronsUpDown className="w-3.5 h-3.5" />}
                            </span>
                          </div>
                        </th>
                        {/* Sortable: seller_name / buyer_name */}
                        <th
                          className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                          onClick={() => toggleSort('buyer_name')}
                          data-testid="ksef-sort-counterparty"
                        >
                          <div className="flex items-center gap-1.5">
                            Kontrahent
                            <span className="opacity-50 group-hover:opacity-100">
                              {sortBy === 'buyer_name'
                                ? (sortOrder === 'desc' ? <ChevronDown className="w-3.5 h-3.5 text-blue-600 opacity-100" /> : <ChevronUp className="w-3.5 h-3.5 text-blue-600 opacity-100" />)
                                : <ChevronsUpDown className="w-3.5 h-3.5" />}
                            </span>
                          </div>
                        </th>
                        {/* Sortable: issue_date */}
                        <th
                          className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                          onClick={() => toggleSort('issue_date')}
                          data-testid="ksef-sort-issue-date"
                        >
                          <div className="flex items-center gap-1.5">
                            Data wystawienia
                            <span className="opacity-50 group-hover:opacity-100">
                              {sortBy === 'issue_date'
                                ? (sortOrder === 'desc' ? <ChevronDown className="w-3.5 h-3.5 text-blue-600 opacity-100" /> : <ChevronUp className="w-3.5 h-3.5 text-blue-600 opacity-100" />)
                                : <ChevronsUpDown className="w-3.5 h-3.5" />}
                            </span>
                          </div>
                        </th>
                        {/* Static: ksef_number */}
                        <th
                          className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                          onClick={() => toggleSort('ksef_number')}
                          data-testid="ksef-sort-ksef-number"
                        >
                          <div className="flex items-center gap-1.5">
                            Nr KSeF
                            <span className="opacity-50 group-hover:opacity-100">
                              {sortBy === 'ksef_number'
                                ? (sortOrder === 'desc' ? <ChevronDown className="w-3.5 h-3.5 text-blue-600 opacity-100" /> : <ChevronUp className="w-3.5 h-3.5 text-blue-600 opacity-100" />)
                                : <ChevronsUpDown className="w-3.5 h-3.5" />}
                            </span>
                          </div>
                        </th>
                        {/* Sortable: net_amount */}
                        <th
                          className="px-3 py-3 text-right font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                          onClick={() => toggleSort('net_amount')}
                          data-testid="ksef-sort-net-amount"
                        >
                          <div className="flex items-center justify-end gap-1.5">
                            Netto (PLN)
                            <span className="opacity-50 group-hover:opacity-100">
                              {sortBy === 'net_amount'
                                ? (sortOrder === 'desc' ? <ChevronDown className="w-3.5 h-3.5 text-blue-600 opacity-100" /> : <ChevronUp className="w-3.5 h-3.5 text-blue-600 opacity-100" />)
                                : <ChevronsUpDown className="w-3.5 h-3.5" />}
                            </span>
                          </div>
                        </th>
                        <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">Typ</th>
                        <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">Status</th>
                        <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-300">Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv, i) => {
                        // For purchases show seller, for sales show buyer
                        const counterparty = inv.direction === 'purchase'
                          ? (inv.seller_name || inv.buyer_name || '-')
                          : (inv.buyer_name || inv.seller_name || '-');
                        const counterpartyNip = inv.direction === 'purchase'
                          ? (inv.seller_nip || inv.buyer_nip || '')
                          : (inv.buyer_nip || inv.seller_nip || '');
                        const displayDate = inv.issue_date
                          ? new Date(inv.issue_date).toLocaleDateString('pl-PL')
                          : (inv.created_at ? new Date(inv.created_at).toLocaleDateString('pl-PL') : '-');

                        return (
                          <tr key={i} className="border-b hover:bg-blue-50/40 dark:hover:bg-slate-700/20 transition-colors" data-testid={`ksef-invoice-row-${i}`}>
                            <td className="px-3 py-2.5">
                              <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">{inv.invoice_number || '-'}</div>
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="text-slate-700 dark:text-slate-300 font-medium text-sm max-w-[180px] truncate" title={counterparty}>{counterparty}</div>
                              {counterpartyNip && <div className="text-xs text-slate-400 font-mono">{counterpartyNip}</div>}
                            </td>
                            <td className="px-3 py-2.5 text-slate-500 text-sm">{displayDate}</td>
                            <td className="px-3 py-2.5">
                              {inv.ksef_number
                                ? <span className="font-mono text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded max-w-[140px] block truncate" title={inv.ksef_number}>{inv.ksef_number}</span>
                                : <span className="text-xs text-slate-300">—</span>
                              }
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <span className="font-semibold text-slate-800 dark:text-slate-200">
                                {inv.net_amount != null ? parseFloat(inv.net_amount).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${inv.direction === 'sales' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                                {inv.direction === 'sales' ? 'Sprzedaz' : 'Zakup'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                inv.status === 'accepted' ? 'bg-green-100 text-green-700' :
                                inv.status === 'sent' ? 'bg-amber-100 text-amber-700' :
                                inv.status === 'downloaded' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {inv.status === 'accepted' ? 'Zaakceptowana' :
                                 inv.status === 'sent' ? 'Wyslana' :
                                 inv.status === 'downloaded' ? 'Pobrana' :
                                 inv.status || 'N/A'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handlePreviewInvoice(inv.id || inv.ksef_number || inv.invoice_reference)}
                                  className="gap-1 text-xs h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`ksef-preview-btn-${i}`}
                                >
                                  <Eye className="w-3 h-3" /> Podglad
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-5 py-3 border-t flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
                    <p className="text-xs text-slate-400">{invoices.length} {invoices.length === 1 ? 'faktura' : invoices.length < 5 ? 'faktury' : 'faktur'} w systemie</p>
                    <p className="text-xs text-slate-400">
                      Sortowanie: <span className="font-medium text-slate-600">{sortBy}</span> ({sortOrder === 'desc' ? 'malejaco' : 'rosnaco'})
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* INVOICE PREVIEW MODAL */}
        {previewInvoice && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setPreviewInvoice(null)}
            data-testid="ksef-preview-modal-overlay"
          >
            <div
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="ksef-preview-modal"
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white dark:bg-slate-900 z-10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white">
                      {previewInvoice.invoice_number || previewInvoice.ksef_number || 'Podglad faktury'}
                    </h3>
                    <p className="text-xs text-slate-400">
                      {previewInvoice.direction === 'sales' ? 'Faktura sprzedazy' : 'Faktura zakupu'}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPreviewInvoice(null)} className="w-8 h-8 p-0" data-testid="ksef-preview-close-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Modal body */}
              <div className="p-6 space-y-5">
                {/* Key details grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Sprzedawca</h4>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                      <p className="font-semibold text-sm text-slate-800 dark:text-white">{previewInvoice.seller_name || '—'}</p>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">NIP: {previewInvoice.seller_nip || '—'}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Nabywca</h4>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                      <p className="font-semibold text-sm text-slate-800 dark:text-white">{previewInvoice.buyer_name || '—'}</p>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">NIP: {previewInvoice.buyer_nip || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Financial details */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Kwoty</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Netto</p>
                      <p className="font-bold text-lg text-slate-800 dark:text-white">
                        {previewInvoice.net_amount != null
                          ? parseFloat(previewInvoice.net_amount).toLocaleString('pl-PL', { minimumFractionDigits: 2 })
                          : '—'} <span className="text-sm font-normal">{previewInvoice.currency || 'PLN'}</span>
                      </p>
                    </div>
                    <div className="text-center border-x border-blue-100 dark:border-blue-800">
                      <p className="text-xs text-slate-500 mb-1">VAT</p>
                      <p className="font-bold text-lg text-slate-800 dark:text-white">
                        {previewInvoice.vat_amount != null
                          ? parseFloat(previewInvoice.vat_amount).toLocaleString('pl-PL', { minimumFractionDigits: 2 })
                          : '—'} <span className="text-sm font-normal">{previewInvoice.currency || 'PLN'}</span>
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Brutto</p>
                      <p className="font-bold text-lg text-blue-700 dark:text-blue-400">
                        {previewInvoice.gross_amount != null
                          ? parseFloat(previewInvoice.gross_amount).toLocaleString('pl-PL', { minimumFractionDigits: 2 })
                          : '—'} <span className="text-sm font-normal">{previewInvoice.currency || 'PLN'}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dates & references */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Nr faktury', value: previewInvoice.invoice_number },
                    { label: 'Nr KSeF', value: previewInvoice.ksef_number, mono: true },
                    { label: 'Data wystawienia', value: previewInvoice.issue_date ? new Date(previewInvoice.issue_date).toLocaleDateString('pl-PL') : null },
                    { label: 'Data wpisu KSeF', value: previewInvoice.invoicing_date ? new Date(previewInvoice.invoicing_date).toLocaleDateString('pl-PL') : null },
                    { label: 'Typ faktury', value: previewInvoice.invoice_type },
                    { label: 'Waluta', value: previewInvoice.currency },
                    { label: 'Kierunek', value: previewInvoice.direction === 'sales' ? 'Sprzedaz' : previewInvoice.direction === 'purchase' ? 'Zakup' : previewInvoice.direction },
                    { label: 'Status', value: previewInvoice.status },
                  ].map(({ label, value, mono }) => value ? (
                    <div key={label} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                      <p className={`text-sm font-medium text-slate-800 dark:text-slate-200 ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
                    </div>
                  ) : null)}
                </div>

                {/* Nr referencyjny */}
                {previewInvoice.invoice_reference && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-400 mb-0.5">Nr referencyjny sesji</p>
                    <p className="text-xs font-mono text-slate-600 dark:text-slate-400 break-all">{previewInvoice.invoice_reference}</p>
                  </div>
                )}

                {/* XML preview */}
                {previewInvoice.xml && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Surowy XML FA(3)
                    </h4>
                    <pre className="p-4 bg-slate-900 text-green-400 rounded-xl text-xs overflow-x-auto max-h-48 leading-relaxed" data-testid="ksef-preview-xml">
                      {previewInvoice.xml}
                    </pre>
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div className="px-6 py-4 border-t bg-slate-50 dark:bg-slate-800/50 flex justify-end rounded-b-2xl">
                <Button variant="outline" size="sm" onClick={() => setPreviewInvoice(null)}>
                  Zamknij
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Loading overlay for preview */}
        {previewLoading && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="ksef-preview-loading">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 flex items-center gap-3 shadow-xl">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-sm font-medium text-slate-700">Ladowanie podgladu...</span>
            </div>
          </div>
        )}

        {/* SEND INVOICE TAB */}
        {activeTab === 'send' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border p-6 shadow-sm">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" /> Wystaw fakture KSeF</h2>

              {!status?.session_active && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <p className="text-sm text-amber-700">Aby wyslac fakture, otworz sesje KSeF w zakladce Konfiguracja.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Nr faktury</label>
                  <Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm({...invoiceForm, invoice_number: e.target.value})}
                    placeholder="FV/2026/02/001" data-testid="inv-number" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Data wystawienia</label>
                  <Input type="date" value={invoiceForm.issue_date} onChange={e => setInvoiceForm({...invoiceForm, issue_date: e.target.value})} data-testid="inv-date" />
                </div>
              </div>

              {/* Seller / Buyer */}
              <div className="grid grid-cols-2 gap-6 mb-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-slate-700">Sprzedawca</h4>
                  <Input value={invoiceForm.seller_name} onChange={e => setInvoiceForm({...invoiceForm, seller_name: e.target.value})} placeholder="Nazwa firmy" data-testid="inv-seller-name" />
                  <Input value={invoiceForm.seller_nip} onChange={e => setInvoiceForm({...invoiceForm, seller_nip: e.target.value.replace(/\D/g, '').slice(0,10)})} placeholder="NIP" data-testid="inv-seller-nip" />
                  <Input value={invoiceForm.seller_address} onChange={e => setInvoiceForm({...invoiceForm, seller_address: e.target.value})} placeholder="Adres" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-slate-700">Nabywca</h4>
                  <Input value={invoiceForm.buyer_name} onChange={e => setInvoiceForm({...invoiceForm, buyer_name: e.target.value})} placeholder="Nazwa firmy" data-testid="inv-buyer-name" />
                  <Input value={invoiceForm.buyer_nip} onChange={e => setInvoiceForm({...invoiceForm, buyer_nip: e.target.value.replace(/\D/g, '').slice(0,10)})} placeholder="NIP" data-testid="inv-buyer-nip" />
                  <Input value={invoiceForm.buyer_address} onChange={e => setInvoiceForm({...invoiceForm, buyer_address: e.target.value})} placeholder="Adres" />
                </div>
              </div>

              {/* Items */}
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Pozycje faktury</h4>
              <div className="space-y-2 mb-3">
                {invoiceForm.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center p-2 bg-slate-50 rounded-lg">
                    <Input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} placeholder="Nazwa" className="flex-1" data-testid={`inv-item-name-${idx}`} />
                    <Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="w-20" placeholder="Ilosc" />
                    <Input value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="w-16" placeholder="J.m." />
                    <Input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="w-28" placeholder="Cena netto" />
                    <select value={item.vat_rate} onChange={e => updateItem(idx, 'vat_rate', e.target.value)} className="w-20 rounded-md border border-slate-200 px-2 py-2 text-sm">
                      <option value="23">23%</option><option value="8">8%</option><option value="5">5%</option><option value="0">0%</option>
                    </select>
                    <span className="text-sm font-medium w-24 text-right">{(parseFloat(item.net_amount) || 0).toFixed(2)} zl</span>
                    {invoiceForm.items.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="w-8 h-8 p-0 text-red-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="gap-1.5 mb-4" data-testid="inv-add-item-btn">
                <Plus className="w-3.5 h-3.5" /> Dodaj pozycje
              </Button>

              {/* Totals */}
              <div className="flex justify-end mb-4">
                <div className="bg-slate-50 rounded-xl p-4 space-y-1 min-w-[200px]">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Netto:</span><span className="font-medium">{netTotal.toFixed(2)} zl</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">VAT:</span><span className="font-medium">{vatTotal.toFixed(2)} zl</span></div>
                  <div className="flex justify-between text-sm font-bold border-t pt-1"><span>Brutto:</span><span>{(netTotal + vatTotal).toFixed(2)} zl</span></div>
                </div>
              </div>

              {/* Payment */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Input value={invoiceForm.payment_date} onChange={e => setInvoiceForm({...invoiceForm, payment_date: e.target.value})} type="date" placeholder="Termin platnosci" />
                <Input value={invoiceForm.bank_account} onChange={e => setInvoiceForm({...invoiceForm, bank_account: e.target.value})} placeholder="Nr konta bankowego" />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={handleGenerateXml} variant="outline" className="gap-2" data-testid="inv-generate-xml-btn">
                  <Eye className="w-4 h-4" /> Generuj XML (podglad)
                </Button>
                <Button onClick={handleSendInvoice} disabled={sendingInvoice || !status?.session_active}
                  className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="inv-send-btn">
                  {sendingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Wyslij do KSeF
                </Button>
              </div>

              {generatedXml && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">Podglad XML FA(3):</h4>
                  <pre className="p-3 bg-slate-900 text-green-400 rounded-xl text-xs overflow-x-auto max-h-64" data-testid="inv-xml-preview">{generatedXml}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CLASSIFY TAB */}
        {activeTab === 'classify' && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border p-6 shadow-sm">
            <h2 className="font-bold text-lg mb-2 flex items-center gap-2"><FileCheck className="w-5 h-5 text-blue-600" /> Klasyfikacja faktury AI</h2>
            <p className="text-sm text-slate-500 mb-4">Wklej dane faktury, a KAI zaklasyfikuje ja ksiegowo.</p>
            <textarea value={classifyText} onChange={e => setClassifyText(e.target.value)}
              placeholder="Np.: Faktura FV/2026/001, sprzedawca: ABC Sp. z o.o., NIP: 1234567890, usluga: hosting, netto: 500 zl, VAT 23%"
              className="w-full h-32 p-4 border rounded-xl text-sm resize-none bg-slate-50" data-testid="ksef-classify-input" />
            <Button onClick={handleClassify} disabled={!classifyText.trim() || classifyLoading}
              className="mt-3 gap-2 bg-blue-600 text-white" data-testid="ksef-classify-btn">
              {classifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Zaklasyfikuj
            </Button>
            {classifyResult && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="ksef-classify-result">
                <p className="text-sm whitespace-pre-wrap">{classifyResult}</p>
              </div>
            )}
          </div>
        )}

        {/* FAQ TAB */}
        {activeTab === 'faq' && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border p-6 shadow-sm">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><HelpCircle className="w-5 h-5 text-indigo-600" /> FAQ - KSeF 2.0</h2>
            <div className="space-y-2">
              {KSEF_FAQ.map((faq, i) => (
                <div key={i} className="border rounded-xl overflow-hidden" data-testid={`ksef-faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full text-left px-5 py-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <span className="font-medium text-sm text-slate-800 pr-4">{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t pt-3">{faq.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
